import { supabase, handleSupabaseError } from '@/lib/supabase';
import { NasabahProfileState } from '@/types';
import { AccountStatus, VerificationStatus } from '@/types/enums';

export interface NasabahProfile {
  id: string;
  userId: string;
  branchId: string;
  accountNumber: string;
  status: AccountStatus;
  balance: number;
  phoneNumber: string | null;
  address: string | null;
  /** Komponen alamat untuk badge "Alamat Terverifikasi". */
  rt: string | null;
  rw: string | null;
  kelurahan: string | null;
  kecamatan: string | null;
  /** Titik lokasi rumah (badge "Lokasi Terverifikasi"). */
  latitude: number | null;
  longitude: number | null;
  /** Nama cabang tempat nasabah terdaftar (turunan dari join branches). */
  branchName: string | null;
  /** Tanggal pendaftaran rekening (nasabah_profiles.created_at). */
  createdAt: Date;
  /** Status verifikasi anggota (menggantikan pemeriksaan kyc_data.approved_at). */
  verificationStatus: VerificationStatus;
  /** Turunan: true jika verificationStatus === VERIFIED */
  isVerified: boolean;
  /** Turunan: true jika PIN transaksi sudah pernah diatur (pin_hash terisi). */
  hasPin: boolean;
  user: {
    fullName: string;
    email: string;
  };
  /** Data dokumen (KTP/selfie URL — disimpan di tabel kyc_data). */
  memberDocuments?: {
    idNumber: string;
    ktpUrl: string;
    selfieUrl: string;
    approvedBy: string | null;
    approvedAt: Date | null;
  };
  /** @deprecated Gunakan memberDocuments */
  kyc?: NasabahProfile['memberDocuments'];
}

class NasabahService {
  /**
   * Get nasabah profile by user ID
   */
  async getProfileByUserId(userId: string): Promise<{ data: NasabahProfile | null; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('nasabah_profiles')
        .select(`
          *,
          users!nasabah_profiles_user_id_fkey(full_name, email),
          branches(name),
          verification_status,
          kyc_data(id_number, ktp_url, selfie_url, approved_by, approved_at)
        `)
        .eq('user_id', userId)
        // Toleran terhadap data lama yang punya >1 profil untuk satu user:
        // utamakan yang sudah punya cabang (branch_id non-null), lalu terlama.
        .order('branch_id', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Nasabah baru (mis. lewat Google OAuth) mungkin belum punya profil.
      // Kembalikan null tanpa error agar UI mengarahkan ke pemilihan cabang,
      // bukan menampilkan PGRST116 ("0 rows").
      return {
        data: data ? this.mapNasabahProfile(data) : null,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Get all nasabah profiles for a branch
   */
  async getNasabahsByBranch(branchId: string): Promise<{ data: NasabahProfile[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('nasabah_profiles')
        .select(`
          *,
          users!nasabah_profiles_user_id_fkey(full_name, email),
          verification_status,
          kyc_data(id_number, ktp_url, selfie_url, approved_by, approved_at)
        `)
        .eq('branch_id', branchId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data.map(this.mapNasabahProfile),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Get pending nasabah registrations (for KYC verification)
   */
  async getPendingRegistrations(branchId: string): Promise<{ data: NasabahProfile[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('nasabah_profiles')
        .select(`
          *,
          users!nasabah_profiles_user_id_fkey(full_name, email),
          verification_status,
          kyc_data(id_number, ktp_url, selfie_url, approved_by, approved_at)
        `)
        .eq('branch_id', branchId)
        .eq('status', AccountStatus.PENDING)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return {
        data: data.map(this.mapNasabahProfile),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Update nasabah profile
   */
  async updateProfile(
    profileId: string,
    updates: Partial<{
      phoneNumber: string;
      address: string;
      rt: string;
      rw: string;
      kelurahan: string;
      kecamatan: string;
      latitude: number | null;
      longitude: number | null;
      pinHash: string;
    }>
  ): Promise<{ error: string | null }> {
    try {
      // Hanya sertakan field yang dikirim agar tidak menimpa kolom lain dengan undefined.
      const payload: Record<string, unknown> = {};
      if (updates.phoneNumber !== undefined) payload.phone_number = updates.phoneNumber;
      if (updates.address !== undefined) payload.address = updates.address;
      if (updates.rt !== undefined) payload.rt = updates.rt;
      if (updates.rw !== undefined) payload.rw = updates.rw;
      if (updates.kelurahan !== undefined) payload.kelurahan = updates.kelurahan;
      if (updates.kecamatan !== undefined) payload.kecamatan = updates.kecamatan;
      if (updates.latitude !== undefined) payload.latitude = updates.latitude;
      if (updates.longitude !== undefined) payload.longitude = updates.longitude;
      if (updates.pinHash !== undefined) payload.pin_hash = updates.pinHash;

      const { error } = await supabase
        .from('nasabah_profiles')
        .update(payload)
        .eq('id', profileId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Verifikasi anggota (setujui pendaftaran).
   */
  async approveRegistration(
    profileId: string,
    approvedBy: string
  ): Promise<{ error: string | null }> {
    try {
      // Update status profil + verification_status
      const { error: profileError } = await supabase
        .from('nasabah_profiles')
        .update({ status: AccountStatus.ACTIVE, verification_status: 'VERIFIED' })
        .eq('id', profileId);

      if (profileError) throw profileError;

      // Update KYC data
      const { error: kycError } = await supabase
        .from('kyc_data')
        .update({
          approved_by: approvedBy,
          approved_at: new Date().toISOString()
        })
        .eq('nasabah_profile_id', profileId);

      if (kycError) throw kycError;

      // Activate user
      const { data: profileData } = await supabase
        .from('nasabah_profiles')
        .select('user_id')
        .eq('id', profileId)
        .single();

      if (profileData) {
        await supabase
          .from('users')
          .update({ is_active: true })
          .eq('id', profileData.user_id);
      }

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Tolak verifikasi anggota.
   */
  async rejectRegistration(
    profileId: string,
    rejectedBy: string,
    reason: string
  ): Promise<{ error: string | null }> {
    try {
      // Update status profil + verification_status
      const { error: profileError } = await supabase
        .from('nasabah_profiles')
        .update({ status: AccountStatus.INACTIVE, verification_status: 'REJECTED' })
        .eq('id', profileId);

      if (profileError) throw profileError;

      // Update KYC data
      const { error: kycError } = await supabase
        .from('kyc_data')
        .update({
          approved_by: rejectedBy,
          approved_at: new Date().toISOString(),
          rejection_reason: reason
        })
        .eq('nasabah_profile_id', profileId);

      if (kycError) throw kycError;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Suspend nasabah account
   */
  async suspendAccount(profileId: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase
        .from('nasabah_profiles')
        .update({ status: AccountStatus.SUSPENDED })
        .eq('id', profileId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Map database record to NasabahProfile type
   */
  private mapNasabahProfile(data: any): NasabahProfile {
    const verificationStatus: VerificationStatus =
      (data.verification_status as VerificationStatus) ?? VerificationStatus.PENDING;

    const memberDocuments = data.kyc_data ? {
      idNumber:   data.kyc_data.id_number,
      ktpUrl:     data.kyc_data.ktp_url,
      selfieUrl:  data.kyc_data.selfie_url,
      approvedBy: data.kyc_data.approved_by,
      approvedAt: data.kyc_data.approved_at ? new Date(data.kyc_data.approved_at) : null,
    } : undefined;

    const branch = Array.isArray(data.branches) ? data.branches[0] : data.branches;

    return {
      id: data.id,
      userId: data.user_id,
      branchId: data.branch_id,
      branchName: branch?.name ?? null,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
      accountNumber: data.account_number,
      status: data.status as AccountStatus,
      balance: parseFloat(data.balance),
      phoneNumber: data.phone_number,
      address: data.address,
      rt: data.rt ?? null,
      rw: data.rw ?? null,
      kelurahan: data.kelurahan ?? null,
      kecamatan: data.kecamatan ?? null,
      latitude: data.latitude ?? null,
      longitude: data.longitude ?? null,
      verificationStatus,
      isVerified: verificationStatus === VerificationStatus.VERIFIED,
      hasPin: !!data.pin_hash,
      user: {
        fullName: data.users.full_name,
        email:    data.users.email,
      },
      memberDocuments,
      kyc: memberDocuments, // backward compat
    };
  }
}

export const nasabahService = new NasabahService();
